import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { explainLocationPermissionOnce } from "./permissionPromptService";

const LOCATION_TIMEOUT_MS = 7000;

function buildLocationResult(status, source, extra = {}) {
  return {
    location_status: status,
    location_source: source,
    location_captured_at: new Date().toISOString(),
    ...extra,
  };
}

function normalizePosition(position, source) {
  const coords = position?.coords || {};
  return buildLocationResult("captured", source, {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy,
    location_captured_at: new Date(position?.timestamp || Date.now()).toISOString(),
  });
}

function normalizeLocationError(error, source) {
  const message = String(error?.message || error || "location_unavailable");
  const lower = message.toLowerCase();
  if (lower.includes("denied") || lower.includes("permission")) {
    return buildLocationResult("denied", source, { location_error: message });
  }
  if (lower.includes("timeout") || error?.code === 3) {
    return buildLocationResult("timeout", source, { location_error: message });
  }
  return buildLocationResult("unavailable", source, { location_error: message });
}

function getWebPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("web_geolocation_unavailable"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: LOCATION_TIMEOUT_MS,
      maximumAge: 0,
    });
  });
}

export async function captureDriverStatusLocation() {
  const source = Capacitor.isNativePlatform() ? "ios_geolocation" : "web_geolocation";
  try {
    if (!explainLocationPermissionOnce()) {
      return buildLocationResult("denied", source, { location_error: "permission_explanation_declined" });
    }
    if (Capacitor.isNativePlatform()) {
      const permission = await Geolocation.requestPermissions();
      if (permission?.location && permission.location !== "granted") {
        return buildLocationResult("denied", source, { location_error: "permission_denied" });
      }
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: LOCATION_TIMEOUT_MS,
        maximumAge: 0,
      });
      return normalizePosition(position, source);
    }

    const position = await getWebPosition();
    return normalizePosition(position, source);
  } catch (error) {
    return normalizeLocationError(error, source);
  }
}
